import React, { PureComponent } from "react";
import * as Icon from "react-feather";

import DataMapperService from "../../services/DataMapperService";
import PortalMenu from "./PortalMenu";
import NativeCell from "./NativeCell";

/**
 * One inline-editable cell in the tracker grid.
 * Renders text input / colored-pill dropdown / centered checkbox based on field_type.
 *
 * PureComponent: re-renders only when its props change (shallow compare). Combined
 * with stable parent handlers, this keeps the grid snappy at 250+ rows.
 */
class TrackerCell extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			editingText: false,
			localText: "",
			dropdownOpen: false,
			// Datasource dropdowns: lazily-loaded options, cached per parent value.
			dsOptions: null,
			dsLoading: false,
			dsLoadedFor: null,
		};
		this._pillRef = React.createRef();
	}

	// ---------- Text ----------

	startTextEdit = () => {
		this.setState({ editingText: true, localText: this.props.value || "" }, () => {
			if (this._inputRef) this._inputRef.focus();
		});
	};

	onTextChange = e => {
		this.setState({ localText: e.target.value });
	};

	commitText = () => {
		const original = this.props.value || "";
		const next = this.state.localText;
		this.setState({ editingText: false });
		if (next !== original) {
			this.props.onChange(next);
		}
	};

	onTextKeyDown = e => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.commitText();
		} else if (e.key === "Escape") {
			this.setState({ editingText: false, localText: this.props.value || "" });
		}
	};

	renderText() {
		const { value } = this.props;
		const { editingText, localText } = this.state;

		if (editingText) {
			return (
				<input
					ref={el => (this._inputRef = el)}
					className="et-cell__text-input"
					value={localText}
					onChange={this.onTextChange}
					onBlur={this.commitText}
					onKeyDown={this.onTextKeyDown}
					data-tracker-focus
				/>
			);
		}

		return (
			<button className="et-cell__text" onClick={this.startTextEdit} data-tracker-focus>
				{value ? <span>{value}</span> : <span className="et-cell__placeholder">—</span>}
			</button>
		);
	}

	// ---------- Dropdown ----------

	toggleDropdown = () => {
		this.setState({ dropdownOpen: !this.state.dropdownOpen });
	};

	closeDropdown = () => {
		this.setState({ dropdownOpen: false });
	};

	pickOption = optKey => {
		this.setState({ dropdownOpen: false });
		this.props.onChange(optKey);
	};

	clearOption = e => {
		e.stopPropagation();
		this.setState({ dropdownOpen: false });
		this.props.onChange("");
	};

	renderDropdown() {
		const { field, value } = this.props;
		const options = Array.isArray(field.options) ? field.options : [];
		const selected = options.find(o => o.key === value);
		const { dropdownOpen } = this.state;

		const pillStyle = selected
			? {
					background: selected.color || "#eef0f4",
					color: this._readableTextColor(selected.color || "#eef0f4"),
			  }
			: undefined;

		return (
			<div className="et-cell__dropdown">
				<button
					className={"et-pill " + (selected ? "" : "et-pill--empty")}
					style={pillStyle}
					onClick={this.toggleDropdown}
					ref={this._pillRef}
					data-tracker-focus
				>
					<span className="et-pill__label">{selected ? selected.label : "—"}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>

				{dropdownOpen && (
					<PortalMenu anchorRef={this._pillRef} onClose={this.closeDropdown}>
							{options.length === 0 && <div className="et-cell__menu-empty">No options configured</div>}
							{options.map(o => (
								<button
									key={o.key}
									className={
										"et-cell__menu-item " +
										(o.key === value ? "et-cell__menu-item--active" : "")
									}
									onClick={() => this.pickOption(o.key)}
								>
									<span
										className="et-cell__menu-swatch"
										style={{ background: o.color || "#eef0f4" }}
									/>
									<span className="et-cell__menu-label">{o.label}</span>
									{o.key === value && <Icon.Check size={12} className="et-cell__menu-check" />}
								</button>
							))}
							{selected && (
								<>
									<div className="et-cell__menu-divider" />
									<button className="et-cell__menu-item et-cell__menu-item--clear" onClick={this.clearOption}>
										<Icon.X size={12} className="et-cell__menu-swatch-clear" />
										<span>Clear</span>
									</button>
								</>
							)}
						</PortalMenu>
					)}
			</div>
		);
	}

	// ---------- Datasource dropdown (entity-backed) ----------

	_datasource() {
		return this.props.field.datasource || null;
	}

	_isDependent() {
		const ds = this._datasource();
		return !!(ds && ds.parentFieldId);
	}

	// The parent selection this dependent dropdown is currently keyed to.
	_parentKey() {
		return this._isDependent() ? this.props.parentValue || "" : "__all__";
	}

	openDatasourceDropdown = () => {
		this.setState({ dropdownOpen: true });
		this._ensureDatasourceOptions();
	};

	_ensureDatasourceOptions() {
		const ds = this._datasource();
		if (!ds) return;
		const parentKey = this._parentKey();

		if (this._isDependent() && !this.props.parentValue) {
			this.setState({ dsOptions: [], dsLoadedFor: parentKey, dsLoading: false });
			return;
		}
		if (this.state.dsLoadedFor === parentKey && this.state.dsOptions) return;

		this.setState({ dsLoading: true });
		DataMapperService.resolveOptions({
			entityKey: ds.entity,
			parentEntityKey: this._isDependent() ? this.props.parentEntityKey : null,
			parentId: this._isDependent() ? this.props.parentValue : null,
		}).then(options => {
			if (this._parentKey() !== parentKey) return; // parent changed meanwhile
			this.setState({ dsOptions: options || [], dsLoadedFor: parentKey, dsLoading: false });
		});
	}

	_datasourceLabelFor(value) {
		if (!value) return null;
		if (Array.isArray(this.state.dsOptions)) {
			const hit = this.state.dsOptions.find(o => String(o.id) === String(value));
			if (hit) return hit.label;
		}
		const labels = this.props.datasourceLabels;
		if (labels && labels[value] !== undefined) return labels[value];
		return null;
	}

	// Engagement datasource on the tracker: the row's own engagement, auto-selected + disabled.
	renderEngagementCell() {
		return (
			<div className="et-cell__dropdown">
				<button className="et-pill et-pill--disabled" disabled title="Set to this row's engagement">
					<span className="et-pill__label">{this.props.engagementName || "—"}</span>
					<Icon.Lock size={10} className="et-pill__chevron" />
				</button>
			</div>
		);
	}

	renderDatasourceDropdown() {
		const ds = this._datasource();
		if (ds && ds.entity === "engagement") return this.renderEngagementCell();

		const { value } = this.props;
		const { dropdownOpen, dsOptions, dsLoading } = this.state;
		const selectedLabel = this._datasourceLabelFor(value);
		const dependentNoParent = this._isDependent() && !this.props.parentValue;

		return (
			<div className="et-cell__dropdown">
				<button
					className={"et-pill " + (selectedLabel ? "" : "et-pill--empty")}
					onClick={this.openDatasourceDropdown}
					ref={this._pillRef}
					data-tracker-focus
				>
					<span className="et-pill__label">{selectedLabel || (value ? "…" : "—")}</span>
					<Icon.ChevronDown size={11} className="et-pill__chevron" />
				</button>

				{dropdownOpen && (
					<PortalMenu anchorRef={this._pillRef} onClose={this.closeDropdown}>
							{dependentNoParent && (
								<div className="et-cell__menu-empty">Select the parent column first</div>
							)}
							{!dependentNoParent && dsLoading && <div className="et-cell__menu-empty">Loading…</div>}
							{!dependentNoParent && !dsLoading && Array.isArray(dsOptions) && dsOptions.length === 0 && (
								<div className="et-cell__menu-empty">No matches</div>
							)}
							{!dependentNoParent &&
								!dsLoading &&
								Array.isArray(dsOptions) &&
								dsOptions.map(o => (
									<button
										key={o.id}
										className={
											"et-cell__menu-item " +
											(String(o.id) === String(value) ? "et-cell__menu-item--active" : "")
										}
										onClick={() => this.pickOption(String(o.id))}
									>
										<span className="et-cell__menu-swatch" style={{ background: "#eef0f4" }} />
										<span className="et-cell__menu-label">{o.label}</span>
										{String(o.id) === String(value) && (
											<Icon.Check size={12} className="et-cell__menu-check" />
										)}
									</button>
								))}
							{value && (
								<>
									<div className="et-cell__menu-divider" />
									<button
										className="et-cell__menu-item et-cell__menu-item--clear"
										onClick={this.clearOption}
									>
										<Icon.X size={12} className="et-cell__menu-swatch-clear" />
										<span>Clear</span>
									</button>
								</>
							)}
						</PortalMenu>
					)}
			</div>
		);
	}

	// Pick black or white text based on background luminance.
	_readableTextColor(hex) {
		if (!hex || hex[0] !== "#" || hex.length < 7) return "#333";
		const r = parseInt(hex.substr(1, 2), 16);
		const g = parseInt(hex.substr(3, 2), 16);
		const b = parseInt(hex.substr(5, 2), 16);
		// Per-channel luminance approximation.
		const lum = (r * 299 + g * 587 + b * 114) / 1000;
		return lum > 160 ? "#2b3340" : "#ffffff";
	}

	// ---------- Checkbox ----------

	toggleChecked = () => {
		const next = this.props.value === "1" ? "0" : "1";
		this.props.onChange(next);
	};

	renderCheckbox() {
		const checked = this.props.value === "1";
		return (
			<button
				className={"et-cell__check " + (checked ? "et-cell__check--on" : "")}
				onClick={this.toggleChecked}
				data-tracker-focus
			>
				{checked && <Icon.Check size={13} />}
			</button>
		);
	}

	componentDidUpdate(prevProps) {
		// If the parent selection changed, invalidate the cached options.
		if (this._isDependent() && prevProps.parentValue !== this.props.parentValue) {
			this.setState({ dsOptions: null, dsLoadedFor: null });
		}
	}

	render() {
		const { field } = this.props;
		switch (field.field_type) {
			case "dropdown":
				return field.datasource ? this.renderDatasourceDropdown() : this.renderDropdown();
			case "checkbox":
				return this.renderCheckbox();
			case "date":
				return <NativeCell type="date" value={this.props.value} onChange={this.props.onChange} />;
			case "text":
			default:
				return this.renderText();
		}
	}
}

export default TrackerCell;
